import { useEffect } from 'react';
import { ACCEPT_INVITATION_LABELS_ID } from '../../config/constants';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Typography, Iconography, Box } from '@lucid/lego';
import { useStore } from '../../store/store';
import { useLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import styles from './AcceptInvitation.module.scss';

/**
 * Maximum allowed length for query parameters to prevent potential attacks
 */
const MAX_PARAM_LENGTH = 500;

/**
 * Validates that a query parameter is a non-empty string within safe length limits
 *
 * @param value - The query parameter value to validate
 * @returns True if the value is a valid, safe string
 */
const isValidParam = (value: string | null): boolean => {
  if (!value || typeof value !== 'string') return false;
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0 || trimmedValue.length > MAX_PARAM_LENGTH) return false;
  // Basic sanitization: ensure no suspicious characters
  if (/<script|javascript:|data:|vbscript:/i.test(trimmedValue)) return false;
  return true;
};

/**
 * AcceptInvitation Page Component
 *
 * @component
 * Handles Auth0 organization invitation acceptance flow with security validations.
 *
 * **Security Features:**
 * - Input validation on all query parameters
 * - XSS prevention through parameter sanitization
 * - No sensitive data logging
 * - Fixed redirect destination (no open redirect vulnerability)
 * - Auth0 PKCE flow for secure authentication
 *
 * **Flow:**
 * 1. User clicks invitation link from email
 * 2. Lands here with invitation, organization, and organization_name params
 * 3. Parameters are validated for security
 * 4. Redirects to Auth0 Universal Login with organization context
 * 5. Auth0 handles password setup for first-time users
 * 6. Auth0 redirects back to app after successful authentication
 *
 * @example
 * ```tsx
 * // Invitation link format:
 * // https://business.stage.dslmotors.com/accept-invitation?invitation=TOKEN&organization=org_XXX&organization_name=NAME
 *
 * <Route path="/accept-invitation" element={<AcceptInvitation />} />
 * ```
 *
 * @returns Loading UI while processing invitation or redirecting
 */
const AcceptInvitation = () => {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const locale = useStore((state) => state.locale);

  const { data: labels } = useLabels({
    id: ACCEPT_INVITATION_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  useEffect(() => {
    const invitation = searchParams.get('invitation');
    const organization = searchParams.get('organization');

    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    // Validate invitation and organization parameters
    const isInvitationValid = isValidParam(invitation);
    const isOrganizationValid = isValidParam(organization);

    // If both parameters are valid and not loading, trigger Auth0 login
    if (isInvitationValid && isOrganizationValid && !isLoading) {
      // SECURITY: No logging of sensitive invitation tokens
      // Redirect to Auth0 Universal Login with organization and invitation
      loginWithRedirect({
        authorizationParams: {
          organization: organization as string, // Organization ID (e.g., org_GBwUHtEP73TWmpoS)
          invitation: invitation as string, // Invitation token
        },
        appState: {
          returnTo: '/', // SECURITY: Fixed redirect - no open redirect vulnerability
        },
      }).catch((error) => {
        // Only log errors, not the invitation token
        console.error('[AcceptInvitation] Error redirecting to Auth0:', error.message);
        navigate('/login', { replace: true });
      });
    } else if (!isLoading && (!isInvitationValid || !isOrganizationValid)) {
      // Invalid or missing required parameters, redirect to login
      // SECURITY: No logging of potentially malicious parameter values
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, navigate, searchParams]);

  return (
    <Box className={styles.login} testId="accept-invitation">
      <Box className={styles.container}>
        <Box className={styles.invitationBox}>
          <Box className={styles.logo}>
            <Iconography category="brand" name="logo" color="black" size={32} />
            <Iconography category="brand" name="bear" color="black" size={32} />
          </Box>
          <Box className={styles.title}>
            <Typography variant="headline" size="medium" color="black" align="center">
              {labels?.['invitation.accept.title'] ?? 'Partner Portal'}
            </Typography>
          </Box>
          <Box className={styles.subtitle}>
            <Typography variant="body" size="medium" color="gray-8" align="center">
              {labels?.['invitation.accept.subtitle'] ??
                'Please wait while we redirect you to set up your account.'}
            </Typography>
          </Box>
          <Box className={styles.processing}>
            <Typography variant="body" size="medium" color="gray-8" align="center">
              {labels?.['invitation.accept.processing'] ?? 'Processing your invitation...'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AcceptInvitation;
